import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultViewType } from '@/constants/scenarioTypes';
import { isMdSymptomDecisionContentCode, isLsCoreNursingProcedureContentCode, isAoDiseaseCareContentCode } from '@/constants/scenarioTypes';
import { EvaluationTable } from '@/pages/results/EvaluationTable';
import { hasSpecialtyPerformanceData, shouldShowEvaluationChecklistFallback } from '@/pages/results/simulationResultBlocks';
import type { WinnerEntry } from '@/pages/results/Top3Winners';
import type { EvaluationType } from '@/types/admin';
import type { ResultViewContextInput } from './resultSessionTypes';
import { resolveSessionResultTableView } from './ResultTableView';
import { renderResultView } from './renderResultView';

/** `수행결과` — assessment·equipment 루브릭은 같은 페이지에서 제외(사용자 요청: 수행만) */
const EXCLUDE_FROM_PERFORMANCE_BLOCK = new Set<string>(['assessment', 'equipment', 'rubric']);

interface ResultBodyProps {
  /** 우선순위: content_code → 시트 result_view → 클래스 category (@see resolveSessionResultTableView) */
  resultViewContext: ResultViewContextInput;
  resultMetadata: Record<string, unknown> | null | undefined;
  isAllParticipantsView?: boolean;
  winners?: WinnerEntry[];
  /** false 이면 중증도분류 확대뷰 토글 숨김 (KTAS 평가모드 권한 제한). 기본 true */
  canShowEnlargedView?: boolean;
  checklistItems: unknown[];
  expandedFeedback: Set<string>;
  onToggleFeedback: (id: string) => void;
}

/**
 * 클래스 시뮬레이션 평가결과 본문.
 * 진입은 항상 `SimulationResults` 단일 페이지 — 여기서 content_code 등으로 ResultViewType을 정한 뒤 해당 result_view 컴포넌트를 렌더한다.
 */
export default function ResultBody({
  resultViewContext,
  resultMetadata,
  isAllParticipantsView,
  winners,
  canShowEnlargedView,
  checklistItems,
  expandedFeedback,
  onToggleFeedback,
}: ResultBodyProps): React.ReactElement {
  const { t } = useTranslation('class');
  const perfTitle = t('simulationResults.tablePerformance');
  const rubricBlockTitle = t('simulationResults.rubricTableSectionTitle');

  const resultTableView: ResultViewType = useMemo(
    () =>
      resolveSessionResultTableView({
        contentCode: resultViewContext.contentCode ?? undefined,
        resultViewType: resultViewContext.resultViewType ?? undefined,
        classCategory: resultViewContext.classCategory,
      }),
    [resultViewContext.contentCode, resultViewContext.resultViewType, resultViewContext.classCategory],
  );

  const mappedBlocks = useMemo(
    () =>
      renderResultView(resultTableView, {
        perfTitle,
        resultMetadata,
        isAllParticipantsView,
        winners,
        contentCode: resultViewContext.contentCode ?? null,
        canShowEnlargedView,
        rubricBlockTitle,
      }),
    [
      resultTableView,
      perfTitle,
      resultMetadata,
      isAllParticipantsView,
      winners,
      resultViewContext.contentCode,
      canShowEnlargedView,
      rubricBlockTitle,
    ],
  );

  const showChecklistFallback = shouldShowEvaluationChecklistFallback({
    resultMetadata,
    resultTableView,
    contentCode: resultViewContext.contentCode ?? null,
    apiChecklistCount: Array.isArray(checklistItems) ? checklistItems.length : 0,
  });
  const performanceChecklistItems = useMemo((): unknown[] => {
    if (!Array.isArray(checklistItems) || checklistItems.length === 0) return [];
    return checklistItems.filter((raw) => {
      const item = raw as { evaluationType?: string } | null | undefined;
      const et = String(item?.evaluationType ?? 'performance').toLowerCase();
      return !EXCLUDE_FROM_PERFORMANCE_BLOCK.has(et);
    });
  }, [checklistItems]);
	const hasPerformanceChecklistRows = performanceChecklistItems.length > 0;
	const hasProcBlocks = hasSpecialtyPerformanceData(resultMetadata);
	/** 절차뷰인데 IV·호흡기 등 **행**이 없고, 아래 `EvaluationTable(수행)`이 곧 뜨면 — `ProcedureResult` 스켈레톤 생략 */
	const suppressProcedureWhenChecklistRenders =
		resultTableView === 'ProcedureView' && !hasProcBlocks && hasPerformanceChecklistRows && showChecklistFallback;
	/** EvaluationTable 뷰인데 실제 체크리스트 데이터가 있으면 빈 스켈레톤 생략 */
	const suppressEvaluationTableSkeleton =
		resultTableView === 'EvaluationTable' && hasPerformanceChecklistRows && showChecklistFallback;
	
	/** ls(핵심간호술기), ao(질환별 환자간호), md(증상별 의사결정), ppe 컨텐츠는 간소화된 UI (태스크 컬럼 숨김, 정답/오답 배지) */
	const isLsContent = isLsCoreNursingProcedureContentCode(resultViewContext.contentCode ?? null);
	const isAoContent = isAoDiseaseCareContentCode(resultViewContext.contentCode ?? null);
	const isMdContent = isMdSymptomDecisionContentCode(resultViewContext.contentCode ?? null);
	const isPpeContent = resultViewContext.contentCode?.toLowerCase() === 'ppe';
	const hideScoreColumn = isLsContent || isAoContent || isMdContent;
	const hideTaskColumn = isLsContent || isAoContent || isMdContent || isPpeContent;
	const useSimpleResultBadge = isLsContent || isAoContent || isMdContent || isPpeContent;
	const hideFeedbackColumn = isPpeContent || isLsContent || isMdContent;
	const useFeedbackAsDescription = false;
	const hideDescriptionColumn = false;
	const useActedPassLogic = isLsContent;

	return (
		<>
			{!suppressProcedureWhenChecklistRenders && !suppressEvaluationTableSkeleton && mappedBlocks}
      {showChecklistFallback && hasPerformanceChecklistRows && !isAoContent && (
        <div className="space-y-6 !mt-6 !mb-4">
          <EvaluationTable
            title={perfTitle}
            type={'performance' as EvaluationType}
            items={performanceChecklistItems as any}
            expandedFeedback={expandedFeedback}
            onToggleFeedback={onToggleFeedback}
            hideScoreColumn={hideScoreColumn}
            hideTaskColumn={hideTaskColumn}
            useSimpleResultBadge={useSimpleResultBadge}
            hideFeedbackColumn={hideFeedbackColumn}
            useFeedbackAsDescription={useFeedbackAsDescription}
            hideDescriptionColumn={hideDescriptionColumn}
            useActedPassLogic={useActedPassLogic}
          />
        </div>
      )}
    </>
  );
}
